/**
 * RankingWeightControls
 * 
 * Compact weight adjustment controls for search result ranking.
 * Can be used in pill preview (pre-search) or results view (post-search).
 * 
 * Features:
 * - Quick preset selector
 * - Expandable individual weight sliders
 * - Real-time preview of weight distribution
 */

import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { 
  ChevronDown, 
  ChevronUp,
  Briefcase, 
  MapPin, 
  Award, 
  Building2, 
  ShieldCheck,
  Sliders,
  Sparkles,
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { 
  WEIGHT_CATEGORIES, 
  WEIGHT_PRESETS, 
  DEFAULT_WEIGHTS,
  mergeWithDefaults,
} from '../../config/ranking-weights';

// Icon mapping for categories
const CATEGORY_ICONS = {
  titleMatch: Briefcase,
  location: MapPin,
  seniority: Award,
  industry: Building2,
  contactQuality: ShieldCheck,
};

// Compact single weight slider
function WeightSlider({ category, value, onChange, compact = false }) {
  const Icon = CATEGORY_ICONS[category.id] || Sliders;
  const fillPercentage = value;
  
  // Color based on weight value
  const getColor = () => {
    if (value >= 80) return '#52dcb3'; // High - teal
    if (value >= 50) return '#5a20ff'; // Medium - purple
    if (value >= 20) return '#94a3b8'; // Low - gray
    return '#e2e8f0'; // Very low
  };
  
  const color = getColor();
  
  if (compact) {
    return (
      <div className="flex items-center gap-2 py-1">
        <Icon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
        <span className="text-xs text-slate-600 w-16 truncate">{category.label}</span>
        <input
          type="range"
          min={0}
          max={100}
          step={10}
          value={value}
          onChange={(e) => onChange(parseInt(e.target.value, 10))}
          className="flex-1 h-1 rounded-full appearance-none cursor-pointer"
          style={{
            background: `linear-gradient(to right, ${color} ${fillPercentage}%, #e2e8f0 ${fillPercentage}%)`,
          }}
        />
        <span 
          className="text-xs font-medium w-8 text-right tabular-nums"
          style={{ color }}
        >
          {value}%
        </span>
      </div>
    );
  }
  
  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-slate-500" />
          <span className="text-sm font-medium text-slate-700">{category.label}</span>
        </div>
        <span 
          className="text-sm font-semibold tabular-nums"
          style={{ color }}
        >
          {value}%
        </span>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        step={10}
        value={value}
        onChange={(e) => onChange(parseInt(e.target.value, 10))}
        className="w-full h-2 rounded-full appearance-none cursor-pointer"
        style={{
          background: `linear-gradient(to right, ${color} ${fillPercentage}%, #e2e8f0 ${fillPercentage}%)`,
        }}
      />
      <p className="text-xs text-slate-400">{category.description}</p>
    </div>
  );
}

// Preset button
function PresetButton({ preset, isActive, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "px-3 py-1.5 rounded-full text-xs font-medium transition-all",
        isActive
          ? "bg-purple-600 text-white shadow-sm"
          : "bg-slate-100 text-slate-600 hover:bg-slate-200"
      )}
    >
      {preset.label}
    </button>
  );
}

// Main component
export default function RankingWeightControls({
  weights = DEFAULT_WEIGHTS,
  onChange,
  variant = 'compact', // 'compact' | 'expanded' | 'minimal' | 'simple'
  showPresets = true,
  className,
}) {
  const [isExpanded, setIsExpanded] = useState(variant === 'expanded');
  const [_activePreset, setActivePreset] = useState('balanced');
  
  const currentWeights = useMemo(() => mergeWithDefaults(weights), [weights]);
  
  // Detect if current weights match any preset
  const matchedPreset = useMemo(() => {
    for (const [id, preset] of Object.entries(WEIGHT_PRESETS)) {
      const matches = Object.keys(preset.weights).every(
        key => currentWeights[key] === preset.weights[key]
      );
      if (matches) return id;
    }
    return null;
  }, [currentWeights]);
  
  const handlePresetClick = useCallback((presetId) => {
    const preset = WEIGHT_PRESETS[presetId];
    if (preset) {
      setActivePreset(presetId);
      onChange?.(preset.weights);
    }
  }, [onChange]);
  
  const handleWeightChange = useCallback((categoryId, value) => {
    setActivePreset(null); // Clear preset when manually adjusting
    onChange?.({
      ...currentWeights,
      [categoryId]: value,
    });
  }, [currentWeights, onChange]);

  const presetStops = useMemo(() => ([
    { id: 'titleFirst', label: 'Title', value: 0 },
    { id: 'locationFirst', label: 'Location', value: 25 },
    { id: 'balanced', label: 'Balanced', value: 50 },
    { id: 'seniorityFirst', label: 'Seniority', value: 75 },
    { id: 'qualityFirst', label: 'Quality', value: 100 },
  ]), []);
  
  const presetValueMap = useMemo(() => {
    return presetStops.reduce((acc, stop) => {
      acc[stop.id] = stop.value;
      return acc;
    }, {});
  }, [presetStops]);
  
  const [simpleValue, setSimpleValue] = useState(
    presetValueMap[matchedPreset] ?? 50
  );
  
  useEffect(() => {
    setSimpleValue(presetValueMap[matchedPreset] ?? 50);
  }, [matchedPreset, presetValueMap]);

  const handleSimpleChange = useCallback((event) => {
    const raw = parseInt(event.target.value, 10);
    const nearest = presetStops.reduce((closest, stop) => {
      const currentDistance = Math.abs(stop.value - raw);
      const closestDistance = Math.abs(closest.value - raw);
      return currentDistance < closestDistance ? stop : closest;
    }, presetStops[0]);
    
    setSimpleValue(nearest.value);
    const preset = WEIGHT_PRESETS[nearest.id];
    if (preset) {
      console.log('🎚️ RankingWeightControls: Simple change', { presetId: nearest.id, weights: preset.weights });
      onChange?.(preset.weights);
    }
  }, [presetStops, onChange]);
  
  if (variant === 'simple') {
    return (
      <div className={cn("rounded-lg border border-slate-200 bg-white/80 px-3 py-3", className)}>
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-purple-500" />
            <span className="text-sm font-medium text-slate-700">Ranking Priority</span>
          </div>
          <span className="text-xs text-purple-600 font-medium">
            {matchedPreset ? WEIGHT_PRESETS[matchedPreset].label : 'Custom'}
          </span>
        </div>
        <div className="relative py-2">
          <input
            type="range"
            min={0}
            max={100}
            step={1}
            value={simpleValue}
            onChange={handleSimpleChange}
            className="w-full h-2 rounded-full appearance-none cursor-pointer hover:opacity-80 transition-opacity"
            style={{
              background: `linear-gradient(to right, #5a20ff ${simpleValue}%, #e2e8f0 ${simpleValue}%)`,
              WebkitAppearance: 'none',
              MozAppearance: 'none',
            }}
          />
          <style>{`
            input[type="range"]::-webkit-slider-thumb {
              -webkit-appearance: none;
              appearance: none;
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: #5a20ff;
              cursor: pointer;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              transition: transform 0.15s ease;
            }
            input[type="range"]::-webkit-slider-thumb:hover {
              transform: scale(1.1);
            }
            input[type="range"]::-webkit-slider-thumb:active {
              transform: scale(1.2);
            }
            input[type="range"]::-moz-range-thumb {
              width: 18px;
              height: 18px;
              border-radius: 50%;
              background: #5a20ff;
              cursor: pointer;
              border: none;
              box-shadow: 0 2px 4px rgba(0,0,0,0.2);
              transition: transform 0.15s ease;
            }
            input[type="range"]::-moz-range-thumb:hover {
              transform: scale(1.1);
            }
            input[type="range"]::-moz-range-thumb:active {
              transform: scale(1.2);
            }
          `}</style>
        </div>
        <div className="flex items-center justify-between text-[10px] text-slate-400">
          {presetStops.map(stop => (
            <span key={stop.id}>{stop.label}</span>
          ))}
        </div>
      </div>
    );
  }
  
  // Minimal variant - just shows current preset or "Custom"
  if (variant === 'minimal') {
    return (
      <div className={cn("flex items-center gap-2", className)}>
        <Sliders className="w-3.5 h-3.5 text-slate-400" />
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
        >
          <span>Ranking: {matchedPreset ? WEIGHT_PRESETS[matchedPreset].label : 'Custom'}</span>
          {isExpanded ? (
            <ChevronUp className="w-3 h-3" />
          ) : (
            <ChevronDown className="w-3 h-3" />
          )}
        </button>
        
        {isExpanded && (
          <div className="absolute top-full left-0 right-0 mt-2 p-3 bg-white rounded-lg shadow-xl border border-slate-200 z-50">
            {/* Presets */}
            {showPresets && (
              <div className="flex flex-wrap gap-2 mb-3 pb-3 border-b border-slate-100">
                {Object.entries(WEIGHT_PRESETS).map(([id, preset]) => (
                  <PresetButton
                    key={id}
                    preset={preset}
                    isActive={matchedPreset === id}
                    onClick={() => handlePresetClick(id)}
                  />
                ))}
              </div>
            )}
            
            {/* Sliders */}
            <div className="space-y-2">
              {Object.values(WEIGHT_CATEGORIES).map(category => (
                <WeightSlider
                  key={category.id}
                  category={category}
                  value={currentWeights[category.id]}
                  onChange={(v) => handleWeightChange(category.id, v)}
                  compact
                />
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }
  
  // Compact variant - collapsible panel
  return (
    <div className={cn("rounded-lg border border-slate-200 bg-white/80", className)}>
      {/* Header */}
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-3 py-2 text-left"
      >
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-purple-500" />
          <span className="text-sm font-medium text-slate-700">Ranking Priority</span>
          {matchedPreset && (
            <span className="px-2 py-0.5 rounded-full text-xs bg-purple-100 text-purple-700">
              {WEIGHT_PRESETS[matchedPreset].label}
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        )}
      </button>
      
      {/* Expanded content */}
      {isExpanded && (
        <div className="px-3 pb-3 space-y-4">
          {/* Preset buttons */}
          {showPresets && (
            <div className="flex flex-wrap gap-2">
              {Object.entries(WEIGHT_PRESETS).map(([id, preset]) => (
                <PresetButton
                  key={id}
                  preset={preset}
                  isActive={matchedPreset === id}
                  onClick={() => handlePresetClick(id)}
                />
              ))}
            </div>
          )}
          
          {/* Weight sliders */}
          <div className="space-y-3 pt-2 border-t border-slate-100">
            {Object.values(WEIGHT_CATEGORIES).map(category => (
              <WeightSlider
                key={category.id}
                category={category}
                value={currentWeights[category.id]}
                onChange={(v) => handleWeightChange(category.id, v)}
                compact={variant === 'compact'}
              />
            ))}
          </div>
          
          {/* Help text */}
          <p className="text-xs text-slate-400 italic">
            Higher weights prioritize that factor in result ordering
          </p>
        </div>
      )}
    </div>
  );
}

// Export individual components for flexible use
export { WeightSlider, PresetButton };

